import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import PricingGroups from "../../components/pricing/PricingGroups";
import PricingMembers from "../../components/pricing/PricingMembers";
import useNotification from "../../components/notifications/useNotifications";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import usePricing from "../../data/usePricing";
import { useFeatureEnabled } from "../../hooks/useFeatureFlag";
import usePricingAccess from "../../hooks/usePricingAccess";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { PricingAlignTarget, PricingGroupBy, PricingGroupKey, PricingGroupOrder } from "../../models/pricing/IPricing";
import { membersRequestFor } from "../../utils/pricingGroups";

/**
 * Find groups of listed and in-stock parts that should share a price, then align the ones that don't.
 */
export default function Pricing() {
  const { setInfo } = usePageNameContext();
  const { features } = useCustomerDetails();
  const pricingEnabled = useFeatureEnabled("eBay Optimisation");
  const { canViewPrices, canEditPrices } = usePricingAccess();
  const { saveHandlers } = useDefaultCRUDHandlers("Prices");
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const { intelligencePricingBulk } = usePartsLookupMutations("");

  const [groupBy, setGroupBy] = useState<PricingGroupBy>("partNumbers");
  const [order, setOrder] = useState<PricingGroupOrder>("widestPrices");
  const [selected, setSelected] = useState<PricingGroupKey | null>(null);
  const [busyGroup, setBusyGroup] = useState<PricingGroupKey | null>(null);
  const [busyKind, setBusyKind] = useState<"intel" | "align" | null>(null);

  const membersRequest = useMemo(() => (selected ? membersRequestFor(selected) : null), [selected]);
  const { groups, members, align, loadMembers } = usePricing(groupBy, membersRequest);

  useEffect(() => {
    setInfo({
      name: "Pricing",
      desc: "Groups of listed and in-stock parts that should share a price. Align the ones that don't.",
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGroupByChange = (next: PricingGroupBy) => {
    setGroupBy(next);
    setSelected(null);
  };

  const refuseAlign = () => {
    if (canEditPrices) return false;
    addNotification({ primaryText: "Your role cannot change prices", variant: "warn" });
    return true;
  };

  const handleAlign = (partIds: string[], target: PricingAlignTarget, customPrice?: number | null) => {
    if (refuseAlign()) return;
    align.mutate(
      { partIds, target, customPrice },
      {
        ...saveHandlers,
        onSuccess: (response) => {
          if (response.success) saveHandlers.onSuccess();
          else addNotification({ primaryText: response.message || "Could not align those parts", variant: "warn" });
        },
      }
    );
  };

  const loadGroupIds = async (key: PricingGroupKey) => {
    const response = await loadMembers(membersRequestFor(key));
    return (response?.data ?? []).map((row) => row.partId);
  };

  const clearBusy = () => {
    setBusyGroup(null);
    setBusyKind(null);
  };

  const handleGetIntelligent = async (key: PricingGroupKey) => {
    setBusyGroup(key);
    setBusyKind("intel");
    try {
      const ids = await loadGroupIds(key);
      if (ids.length === 0) {
        addNotification({ primaryText: "No parts in this group", variant: "warn" });
        return;
      }

      const response = await intelligencePricingBulk.mutateAsync(ids);
      const results = response?.data ?? [];
      const succeeded = results.filter((item) => (item.results ?? []).length > 0).length;
      const failed = results.length - succeeded;
      await queryClient.invalidateQueries(["pricing"]);
      await queryClient.invalidateQueries(["intelligenceReview"]);
      addNotification({
        primaryText: `Intelligence updated for ${succeeded} part${succeeded === 1 ? "" : "s"}${failed > 0 ? `, ${failed} returned no data` : ""}`,
        variant: failed > 0 && succeeded === 0 ? "warn" : "success",
      });
    } catch {
      addNotification({ primaryText: "Intelligence pricing failed", variant: "error" });
    } finally {
      clearBusy();
    }
  };

  const handleAlignMany = async (keys: PricingGroupKey[], target: PricingAlignTarget) => {
    if (refuseAlign()) return;
    setBusyKind("align");
    if (keys.length === 1) setBusyGroup(keys[0]);
    try {
      let applied = 0;
      for (const key of keys) {
        const ids = await loadGroupIds(key);
        if (ids.length === 0) continue;
        const response = await align.mutateAsync({ partIds: ids, target });
        if (response.success) applied += 1;
      }
      if (applied === 0) {
        addNotification({ primaryText: "No parts in these groups", variant: "warn" });
      } else {
        saveHandlers.onSuccess();
      }
    } catch {
      addNotification({ primaryText: "Could not align the selected groups", variant: "error" });
    } finally {
      clearBusy();
    }
  };

  const handleAlignGroup = (key: PricingGroupKey, target: PricingAlignTarget) => handleAlignMany([key], target);

  if (features.isLoading) {
    return <LoadingWheel />;
  }

  if (!canViewPrices) {
    return (
      <div className="mt-8 rounded-md border border-gray-200 bg-white p-6 text-center">
        <h2 className="text-lg font-medium text-gray-900">Prices are hidden for your role</h2>
        <p className="mt-2 text-sm text-gray-600">Ask an admin to change your role's pricing access to use this page.</p>
      </div>
    );
  }

  if (!pricingEnabled) {
    return (
      <div className="mt-8 rounded-md border border-gray-200 bg-white p-6 text-center">
        <h2 className="text-lg font-medium text-gray-900">Pricing is not enabled</h2>
        <p className="mt-2 text-sm text-gray-600">
          This feature is not enabled for your account. Contact support if you&apos;d like it turned on.
        </p>
      </div>
    );
  }

  const handleGetIntelligentMany = async (keys: PricingGroupKey[]) => {
    setBusyKind("intel");
    try {
      const idSets = await Promise.all(keys.map((key) => loadGroupIds(key)));
      const ids = [...new Set(idSets.flat())];
      if (ids.length === 0) {
        addNotification({ primaryText: "No parts in these groups", variant: "warn" });
        return;
      }

      const response = await intelligencePricingBulk.mutateAsync(ids);
      const results = response?.data ?? [];
      const succeeded = results.filter((item) => (item.results ?? []).length > 0).length;
      const failed = results.length - succeeded;
      await queryClient.invalidateQueries(["pricing"]);
      await queryClient.invalidateQueries(["intelligenceReview"]);
      addNotification({
        primaryText: `Intelligence updated for ${succeeded} part${succeeded === 1 ? "" : "s"}${failed > 0 ? `, ${failed} returned no data` : ""}`,
        variant: failed > 0 && succeeded === 0 ? "warn" : "success",
      });
    } catch {
      addNotification({ primaryText: "Intelligence pricing failed", variant: "error" });
    } finally {
      clearBusy();
    }
  };

  return (
    <div className="mt-4 flex h-[calc(100vh-9rem)] min-h-[28rem] flex-col gap-4">
      <PricingGroups
        stats={groups.data?.data}
        isLoading={groups.isLoading}
        isError={groups.isError}
        groupBy={groupBy}
        order={order}
        selected={selected}
        busyGroup={busyGroup}
        busyKind={busyKind}
        onGroupByChange={handleGroupByChange}
        onOrderChange={setOrder}
        onSelect={setSelected}
        onGetIntelligent={handleGetIntelligent}
        onGetIntelligentMany={handleGetIntelligentMany}
        onAlignGroup={handleAlignGroup}
        onAlignMany={handleAlignMany}
      />
      {selected && (
        <PricingMembers
          members={members.data?.data}
          isLoading={members.isLoading}
          isAligning={align.isLoading}
          onAlign={handleAlign}
        />
      )}
    </div>
  );
}
